import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Dialog, Button } from '@/design-system';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import type { FunnelSummary } from '@/contracts/funnel';
import type { TemplateSummary } from '@/contracts/templates';

export interface UpdateFromFunnelDialogProps {
  template: TemplateSummary | null;
  onClose: () => void;
  onUpdated: () => void;
}

/** SPEC-010 §59/§21/AC-045/AC-046 — explicit "replace content" action, never automatic. */
export function UpdateFromFunnelDialog({ template, onClose, onUpdated }: UpdateFromFunnelDialogProps) {
  const [funnels, setFunnels] = useState<FunnelSummary[]>([]);
  const [funnelId, setFunnelId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!template) return;
    setFunnelId('');
    setError(null);
    Promise.all([
      dashboardApi.listFunnels(new URLSearchParams({ status: 'active', limit: '100' })),
      dashboardApi.listFunnels(new URLSearchParams({ status: 'archived', limit: '100' })),
    ]).then(([active, archived]) => setFunnels([...active.items, ...archived.items]));
  }, [template]);

  if (!template) return null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!template || !funnelId) return;
    setSubmitting(true);
    setError(null);
    try {
      await dashboardApi.replaceCustomTemplateFromFunnel(template.id, funnelId);
      onClose();
      onUpdated();
    } catch {
      setError('Não foi possível atualizar o modelo a partir desse funil. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={Boolean(template)} onClose={onClose} title={`Atualizar "${template.name}" a partir de um funil`}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary">
          Isso substitui a estrutura do modelo pelo conteúdo atual do funil selecionado e aumenta sua
          versão. Os funis já criados a partir deste modelo não são afetados.
        </p>
        <label className="flex flex-col gap-1 text-sm text-text-secondary">
          Funil de origem
          <select
            value={funnelId}
            onChange={(e) => setFunnelId(e.target.value)}
            required
            disabled={submitting}
            className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text-primary"
          >
            <option value="" disabled>
              Selecionar um funil…
            </option>
            {funnels.map((funnel) => (
              <option key={funnel.id} value={funnel.id}>
                {funnel.name}
              </option>
            ))}
          </select>
        </label>
        {error ? (
          <p role="alert" className="text-sm text-[#f08484]">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={submitting || !funnelId}>
            {submitting ? 'Atualizando…' : 'Atualizar modelo'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
